import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { US_LOCATIONS, OTHER_LOCATION } from "../lib/locations";
import MemberBadge from "../components/MemberBadge";

/**
 * Session 58 · Wave 2 · Early Adopter signup landing.
 *
 * Distinct from `/signup` — this page is the marketing surface for the
 * Wave 2 rollout: silver Early Adopter branding, permanent-badge preview,
 * and the same legal + registration contract as generic signup.
 *
 * Submitting posts `cohort: "wave2"` to /api/auth/register. Backend
 * behavior:
 *   · users.member_type = "early_adopter" (default for non-admin signups)
 *   · users.cohort      = "wave2"       (set by alpha_ops.mark_invite_accepted)
 *   · welcome email routed to the Early Adopter template (server.py)
 *   · admin fanout notification fires so Cam sees each Wave 2 join
 *   · alpha_invites row (if any matched by email) flipped to accepted
 */
export default function EarlyAdopterSignup() {
  const { user, register, googleLogin } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const invitedEmail = (searchParams.get("invite") || "").toLowerCase().trim();

  const [form, setForm] = useState({
    name: "",
    email: invitedEmail,
    password: "",
    city: "Washington, DC",
    profession_type: "",
  });
  const [locationChoice, setLocationChoice] = useState("Washington, DC");
  const [otherCountry, setOtherCountry] = useState("");
  const [professions, setProfessions] = useState([]);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [legal, setLegal] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/options/professions").then((r) => setProfessions(r.data)).catch(() => {});
    api.get("/legal/current").then((r) => setLegal(r.data.documents)).catch(() => {});
  }, []);

  const legalReady = Boolean(legal?.terms?.version && legal?.privacy?.version);
  const canSubmit = useMemo(
    () => acceptTerms && acceptPrivacy && legalReady && !busy && form.name && form.email && form.password,
    [acceptTerms, acceptPrivacy, legalReady, busy, form.name, form.email, form.password],
  );

  if (user) return <Navigate to="/profile" replace />;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!acceptTerms || !acceptPrivacy) {
      setErr("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    if (!legalReady) {
      setErr("Legal documents haven't loaded yet — please try again in a moment.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await register({
        ...form,
        accepted_terms: true,
        accepted_privacy: true,
        accepted_terms_version: legal.terms.version,
        accepted_privacy_version: legal.privacy.version,
        invite_email: invitedEmail || null,
        cohort: "wave2",
      });
      nav("/profile?welcome=early_adopter");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const perks = [
    { t: "Permanent access", d: "Your membership is locked in from day one — no expiration." },
    { t: "Priority access", d: "First look at new workspaces and Métier Hours café partners as they come online." },
    { t: "Direct line to the team", d: "Feedback goes straight to Cam and the team — real humans, no support tickets." },
    { t: "Help us build this", d: "Your feedback in these early weeks shapes the roadmap more than anyone joining later. We're listening." },
  ];

  return (
    <div data-testid="early-adopter-signup-page" className="ea-signup">
      {/* Left / hero panel */}
      <aside className="ea-hero">
        <div className="ea-hero-inner">
          <div className="ea-logo">
            <img src="/brand/metier-logo-gold.png" alt="Métier" />
          </div>

          <div className="ea-eyebrow">Now open</div>
          <h1 className="ea-headline">
            Join <em>Métier</em>.
          </h1>
          <p className="ea-lede">
            Métier is an intentionally small, curated network of boutique workspaces,
            Métier Hours café perks, and a community built for professionals with
            flexible schedules. We&#39;re still early — and we&#39;re looking for people
            who want to help shape what it becomes.
          </p>

          <div className="ea-perks">
            <div className="ea-perks-title">What you get</div>
            <ul>
              {perks.map((p) => (
                <li key={p.t} data-testid={`ea-perk-${p.t.toLowerCase().replace(/\s+/g, "-")}`}>
                  <strong>{p.t}</strong>
                  <span>{p.d}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="ea-hero-foot">
            <span>workmetier.com</span>
            <span>· Métier Member</span>
          </div>
        </div>
      </aside>

      {/* Right / form panel */}
      <main className="ea-form-panel">
        <div className="ea-card">
          <div className="ea-card-eyebrow">Claim your spot</div>
          <h2 className="ea-card-h2">
            Become a <em>Métier Member</em>.
          </h2>
          <p className="ea-card-sub">One minute. No credit card. You&#39;re in the moment you sign up.</p>

          {invitedEmail && (
            <div data-testid="ea-invite-banner" className="ea-invite-banner">
              <div className="ea-invite-eyebrow">Métier invitation</div>
              You&#39;re invited as <strong>{invitedEmail}</strong>. Complete signup to claim your spot.
            </div>
          )}

          <button
            type="button"
            className="ea-google-btn"
            onClick={googleLogin}
            data-testid="ea-google-signup"
          >
            <span>Continue with Google</span>
          </button>
          <div className="ea-divider"><span>or with email</span></div>

          <form onSubmit={onSubmit} className="ea-form" data-testid="ea-signup-form">
            <label className="ea-field">
              <span>Full name</span>
              <input
                value={form.name}
                onChange={update("name")}
                required
                autoComplete="name"
                data-testid="ea-signup-name"
              />
            </label>

            <label className="ea-field">
              <span>Email</span>
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                autoComplete="email"
                data-testid="ea-signup-email"
              />
            </label>

            <label className="ea-field">
              <span>Password</span>
              <input
                type="password"
                value={form.password}
                onChange={update("password")}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="ea-signup-password"
              />
            </label>

            <label className="ea-field">
              <span>Primary location</span>
              <select
                value={locationChoice}
                onChange={(e) => {
                  const val = e.target.value;
                  setLocationChoice(val);
                  if (val !== OTHER_LOCATION) {
                    setForm((f) => ({ ...f, city: val }));
                  } else {
                    setForm((f) => ({ ...f, city: otherCountry }));
                  }
                }}
                data-testid="ea-signup-city"
              >
                {US_LOCATIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
                <option value={OTHER_LOCATION}>Other (outside US)…</option>
              </select>
            </label>

            {locationChoice === OTHER_LOCATION && (
              <label className="ea-field" data-testid="ea-signup-country-field">
                <span>Country</span>
                <input
                  type="text"
                  value={otherCountry}
                  onChange={(e) => {
                    setOtherCountry(e.target.value);
                    setForm((f) => ({ ...f, city: e.target.value }));
                  }}
                  placeholder="e.g. United Kingdom"
                  required
                  data-testid="ea-signup-country"
                />
              </label>
            )}

            <label className="ea-field">
              <span>How do you work?</span>
              <select
                value={form.profession_type}
                onChange={update("profession_type")}
                data-testid="ea-signup-profession"
              >
                <option value="">Prefer not to say</option>
                {professions.map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </select>
            </label>

            {err && <div className="ea-error" data-testid="ea-signup-error">{err}</div>}

            <div className="ea-legal" data-testid="ea-signup-legal-block">
              <label>
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  data-testid="ea-signup-terms-checkbox"
                />
                <span>
                  I have read and agree to the{" "}
                  <Link to="/legal/terms" target="_blank" rel="noopener" data-testid="ea-signup-terms-link">
                    Terms of Service
                  </Link>
                  {legal?.terms && <> (v{legal.terms.version})</>}.
                </span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  data-testid="ea-signup-privacy-checkbox"
                />
                <span>
                  I acknowledge the{" "}
                  <Link to="/legal/privacy" target="_blank" rel="noopener" data-testid="ea-signup-privacy-link">
                    Privacy Policy
                  </Link>
                  {legal?.privacy && <> (v{legal.privacy.version})</>}.
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="ea-submit"
              disabled={!canSubmit}
              data-testid="ea-signup-submit"
            >
              {busy ? "Creating account…" : "Join Métier →"}
            </button>
          </form>

          <p className="ea-footnote">
            Already a member? <Link to="/login" data-testid="ea-link-login">Sign in →</Link>
          </p>
          <p className="ea-footnote-sm">
            Have an invite? <Link to="/signup" data-testid="ea-link-general-signup">Standard signup →</Link>
          </p>
        </div>
      </main>

      <style>{`
        .ea-signup {
          display: grid;
          grid-template-columns: 1fr 1fr;
          min-height: 100vh;
          background: #FAF8F3;
          color: #1B2340;
          font-family: 'Jost', Helvetica, Arial, sans-serif;
        }
        .ea-hero {
          position: relative;
          background:
            radial-gradient(1200px 800px at -10% -20%, rgba(184,145,58,0.10), transparent 60%),
            radial-gradient(900px 600px at 110% 110%, rgba(122,128,146,0.16), transparent 55%),
            linear-gradient(155deg, #171E38 0%, #1B2340 60%, #232B4F 100%);
          color: #FAF8F3;
          padding: 56px 56px 40px;
          display: flex;
          align-items: flex-start;
          overflow: hidden;
        }
        .ea-hero::before {
          content: "";
          position: absolute;
          inset: 0;
          background-image:
            radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.08), transparent 50%),
            radial-gradient(1px 1px at 70% 60%, rgba(255,255,255,0.06), transparent 50%),
            radial-gradient(1px 1px at 40% 80%, rgba(255,255,255,0.07), transparent 50%);
          pointer-events: none;
        }
        .ea-hero-inner {
          position: relative;
          max-width: 520px;
        }
        .ea-logo { margin-bottom: 40px; }
        .ea-logo img { height: 32px; opacity: 0.95; }
        .ea-eyebrow {
          letter-spacing: 0.24em;
          text-transform: uppercase;
          font-size: 11px;
          color: #B8913A;
          font-weight: 700;
          margin-bottom: 14px;
        }
        .ea-headline {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-weight: 500;
          font-size: 56px;
          line-height: 1.05;
          margin: 0 0 20px;
          color: #FAF8F3;
        }
        .ea-headline em {
          font-style: italic;
          color: #E1E3E8;
          background: linear-gradient(135deg,#E9EBEF 0%,#B1B7C4 55%,#8188A0 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 500;
        }
        .ea-lede {
          font-size: 15.5px;
          line-height: 1.65;
          color: rgba(250,248,243,0.78);
          margin: 0 0 32px;
          max-width: 460px;
        }
        .ea-badge-preview {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          background: rgba(250,248,243,0.06);
          border: 1px solid rgba(250,248,243,0.14);
          border-radius: 14px;
          margin-bottom: 28px;
          backdrop-filter: blur(8px);
        }
        .ea-badge-caption {
          font-size: 12.5px;
          color: rgba(250,248,243,0.72);
          font-style: italic;
        }
        .ea-perks {
          margin-top: 8px;
          padding-top: 26px;
          border-top: 1px solid rgba(250,248,243,0.10);
        }
        .ea-perks-title {
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-size: 11px;
          color: #B8913A;
          font-weight: 700;
          margin-bottom: 14px;
        }
        .ea-perks ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          gap: 14px;
        }
        .ea-perks li {
          display: grid;
          gap: 3px;
          padding-left: 18px;
          position: relative;
          color: rgba(250,248,243,0.86);
        }
        .ea-perks li::before {
          content: "◆";
          position: absolute;
          left: 0;
          top: 2px;
          color: #B8913A;
          font-size: 10px;
        }
        .ea-perks strong {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-weight: 500;
          font-size: 17px;
          color: #FAF8F3;
        }
        .ea-perks span {
          font-size: 13.5px;
          line-height: 1.55;
          color: rgba(250,248,243,0.7);
        }
        .ea-hero-foot {
          margin-top: 40px;
          font-size: 12px;
          color: rgba(250,248,243,0.45);
          display: flex;
          gap: 8px;
          letter-spacing: 0.06em;
        }

        .ea-form-panel {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px 40px;
        }
        .ea-card {
          width: 100%;
          max-width: 480px;
          background: #ffffff;
          border: 1px solid rgba(27,35,64,0.08);
          border-radius: 16px;
          padding: 40px 40px 32px;
          box-shadow: 0 30px 60px -30px rgba(27,35,64,0.20);
        }
        .ea-card-eyebrow {
          letter-spacing: 0.22em;
          text-transform: uppercase;
          font-size: 11px;
          color: #B8913A;
          font-weight: 700;
        }
        .ea-card-h2 {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-weight: 500;
          font-size: 36px;
          line-height: 1.1;
          margin: 10px 0 6px;
          color: #1B2340;
        }
        .ea-card-h2 em {
          font-style: italic;
          color: #B8913A;
          font-weight: 500;
        }
        .ea-card-sub {
          font-size: 13.5px;
          color: rgba(27,35,64,0.65);
          margin: 0 0 18px;
        }

        .ea-invite-banner {
          background: rgba(184,145,58,0.10);
          border: 1px solid rgba(184,145,58,0.4);
          border-radius: 10px;
          padding: 12px 14px;
          margin: 4px 0 18px;
          font-size: 13px;
          color: #1B2340;
          line-height: 1.5;
        }
        .ea-invite-eyebrow {
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #B8913A;
          font-weight: 600;
          margin-bottom: 2px;
        }

        .ea-google-btn {
          width: 100%;
          padding: 12px 16px;
          background: #ffffff;
          border: 1px solid rgba(27,35,64,0.18);
          border-radius: 999px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.04em;
          color: #1B2340;
          transition: background 0.15s, border-color 0.15s;
        }
        .ea-google-btn:hover {
          background: #FAF8F3;
          border-color: rgba(27,35,64,0.35);
        }
        .ea-divider {
          position: relative;
          text-align: center;
          margin: 16px 0 14px;
        }
        .ea-divider::before, .ea-divider::after {
          content: "";
          position: absolute;
          top: 50%;
          width: calc(50% - 40px);
          height: 1px;
          background: rgba(27,35,64,0.10);
        }
        .ea-divider::before { left: 0; }
        .ea-divider::after { right: 0; }
        .ea-divider span {
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: rgba(27,35,64,0.5);
          background: #ffffff;
          padding: 0 12px;
        }

        .ea-form { display: grid; gap: 12px; }
        .ea-field { display: grid; gap: 5px; }
        .ea-field > span {
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(27,35,64,0.65);
          font-weight: 600;
        }
        .ea-field input, .ea-field select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid rgba(27,35,64,0.16);
          border-radius: 8px;
          font-size: 14px;
          background: #ffffff;
          box-sizing: border-box;
          font-family: inherit;
          color: #1B2340;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .ea-field input:focus, .ea-field select:focus {
          outline: none;
          border-color: #B8913A;
          box-shadow: 0 0 0 3px rgba(184,145,58,0.14);
        }

        .ea-error {
          background: #FDF1EC;
          border: 1px solid #E5A0A0;
          color: #7A1B22;
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 12.5px;
        }

        .ea-legal {
          display: grid;
          gap: 8px;
          padding: 12px 0 4px;
          font-size: 12.5px;
          color: rgba(27,35,64,0.75);
          line-height: 1.5;
        }
        .ea-legal label {
          display: flex;
          gap: 8px;
          align-items: flex-start;
        }
        .ea-legal input[type="checkbox"] { margin-top: 3px; }
        .ea-legal a {
          color: #B8913A;
          text-decoration: none;
          font-weight: 600;
        }
        .ea-legal a:hover { text-decoration: underline; }

        .ea-submit {
          width: 100%;
          padding: 13px 20px;
          margin-top: 6px;
          background: linear-gradient(135deg, #1B2340 0%, #2A3560 100%);
          color: #FAF8F3;
          border: 1px solid rgba(184,145,58,0.55);
          border-radius: 999px;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.04em;
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s;
          box-shadow: 0 4px 16px -4px rgba(27,35,64,0.35);
        }
        .ea-submit:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 24px -6px rgba(27,35,64,0.42);
        }
        .ea-submit:disabled {
          opacity: 0.55;
          cursor: not-allowed;
          box-shadow: none;
        }

        .ea-footnote {
          text-align: center;
          margin-top: 20px;
          font-size: 13px;
          color: rgba(27,35,64,0.65);
        }
        .ea-footnote a { color: #B8913A; text-decoration: none; font-weight: 600; }
        .ea-footnote-sm {
          text-align: center;
          margin-top: 6px;
          font-size: 11.5px;
          color: rgba(27,35,64,0.45);
        }
        .ea-footnote-sm a { color: rgba(27,35,64,0.65); text-decoration: underline; }

        @media (max-width: 900px) {
          .ea-signup { grid-template-columns: 1fr; }
          .ea-hero { padding: 40px 28px 30px; }
          .ea-headline { font-size: 40px; }
          .ea-form-panel { padding: 28px 20px 40px; }
          .ea-card { padding: 28px 24px 24px; }
        }
      `}</style>
    </div>
  );
}
